import classNames from 'clsx';
import { ReactComponent as Icon20Stars } from 'img/quota-landing/aiTariff/stars_20.svg';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { isAiOrganizeTipOnStars, PROMO_STARS_TIP_ID } from 'reactApp/appHelpers/featuresHelpers/features/fakedoorNeuroOrganizePromo';
import { isDialogVisible } from 'reactApp/modules/dialog/dialog.selectors';
import { sendDwhButton, sendDwhTooltip } from 'reactApp/modules/dwh/dwh.helpers';
import type { EViewMode } from 'reactApp/modules/settings/settings.types';
import type { EStorageType } from 'reactApp/modules/storage/storage.types';
import { DataListControl } from 'reactApp/ui/DataListControl/DataListControl';
import { renderFakedoorNeuroOrganizerDialog } from 'reactApp/ui/FakedoorNeuroOrganizePromo/Popup/FakedoorNeuroOrganizerDialog';
import { checkTooltipCanBeShown } from 'reactApp/ui/FakedoorNeuroOrganizePromo/utils';
import { FloatingTooltip } from 'reactApp/ui/FloatingTooltip/FloatingTooltip';
import { ETooltipPlacement, ETooltipSize } from 'reactApp/ui/FloatingTooltip/FloatingTooltip.types';
import { Hint } from 'reactApp/ui/Hint/Hint';
import { sendXray } from 'reactApp/utils/ga';

import styles from './AiOrganize.css';

const name = 'mnz_ai_organize_fakedr_lst_btn';
const nameTooltip = 'mnz_ai_organize_fakedr_lst_tltip';

const sendAiIconTechRadar = (storage: EStorageType, action: 'show' | 'click' = 'show') => {
    sendXray(['stars', 'ai_organize', action, storage]);
};

const sendTipTechRadar = (storage: EStorageType, action: 'show' | 'click' | 'close' = 'show') => {
    sendXray(['tip', 'ai_organize', action, storage]);
};

interface Props {
    animate?: boolean;
    viewType: EViewMode;
    className?: string;
    itemId?: string;
    noTip?: boolean;
    storage: EStorageType;
    size?: 30 | 28;
    rootClassName?: string;
}

// Оптимизация, чтобы постоянно не лазить в стор - тултип надо показать только на одном элементе (первый доступный)
let itemIdForTooltip: string | undefined;

export const AiOrganize = memo<Props>(function AiOrganize({
    animate = false,
    viewType,
    className,
    rootClassName,
    itemId,
    noTip = false,
    storage,
    size,
}) {
    const [showTip, setShowTip] = useState(false);
    const ref = useRef<HTMLDivElement | null>(null);
    const isAnyDialogVisible = useSelector(isDialogVisible);

    useEffect(() => {
        sendDwhButton({ action: 'show', name, source: storage });
        sendAiIconTechRadar(storage);
    }, [itemId, storage]);

    useEffect(() => {
        if (noTip || !isAiOrganizeTipOnStars || isAnyDialogVisible) {
            return;
        }
        if (itemIdForTooltip === undefined) {
            itemIdForTooltip = itemId;
        }
        if (itemIdForTooltip !== itemId) {
            return;
        }

        if (checkTooltipCanBeShown(PROMO_STARS_TIP_ID)) {
            setShowTip(true);
        }
    }, [itemId, noTip, isAnyDialogVisible]);

    const handleClick = useCallback(() => {
        sendDwhButton({ action: 'click', name, source: storage });
        sendAiIconTechRadar(storage, 'click');

        renderFakedoorNeuroOrganizerDialog({ isByClick: true });
    }, [storage]);

    const closeHandler = useCallback(() => {
        sendDwhTooltip({ action: 'close', name: nameTooltip, source: storage });
        sendTipTechRadar(storage, 'close');
        setShowTip(false);
    }, [storage]);

    const showHandler = useCallback(() => {
        sendDwhTooltip({ action: 'show', name: nameTooltip, source: storage });
        sendTipTechRadar(storage);
    }, [storage]);

    const primaryClickHandler = useCallback(() => {
        sendDwhTooltip({ action: 'click', name: nameTooltip, source: storage });
        sendTipTechRadar(storage, 'click');

        renderFakedoorNeuroOrganizerDialog({ isByClick: true });
        setShowTip(false);
    }, [storage]);

    return (
        <div
            className={classNames(styles.root, rootClassName, {
                [styles[`root_${size}`]]: !!size,
            })}
        >
            <div ref={ref} className={styles.tip} />
            <Hint text="Что внутри">
                <div>
                    <DataListControl.New
                        itemId={itemId}
                        id="aiOrganize-icon"
                        type={'aiOrganize'}
                        viewType={viewType}
                        animate={animate}
                        className={className}
                        onClick={handleClick}
                    >
                        <Icon20Stars width={18} height={18} className={styles.icon} />
                    </DataListControl.New>
                </div>
            </Hint>
            {showTip && (
                <FloatingTooltip
                    target={ref?.current}
                    className={styles.tipRoot}
                    contentClassName={styles.content}
                    size={ETooltipSize.medium}
                    text={
                        <div>
                            Нейросеть подскажет, что
                            <br />
                            внутри папки или документа —
                            <br />
                            не придётся открывать
                        </div>
                    }
                    buttonText={'Узнать больше'}
                    placement={ETooltipPlacement.bottom}
                    closeOnOutsideClick={true}
                    qaId={'aiOrganize-icon-tip'}
                    onClose={closeHandler}
                    onShow={showHandler}
                    onClick={primaryClickHandler}
                    buttonFitContent
                    usePortal
                    autoUpdateOnResize
                />
            )}
        </div>
    );
});
